<template>
  <div class="quick-pick-filter">
    <v-btn-toggle 
        v-if="toggles"
      multiple
      v-model="quickPickSelection"
    >
      <v-hover
        v-for="item in items"
        :key="item.value"
        v-slot="{ hover }"
      >
        <v-btn
          :class="{ 'on-hover': hover }"
          class="selection-button"
          :value="item.value"
          :ripple="false"
          @dblclick="toggleOnly(item.value, $event)"
        >
          <span :class="item.colorClass" class="qp-btn-icon" v-if="item.icon && quickPickSelection.includes(item.value)"><i :class="item.icon"></i></span>
          <span :class="item.colorClass" class="qp-btn-icon" v-if="item.disabledIcon && !quickPickSelection.includes(item.value)"><i :class="item.disabledIcon"></i></span>
          <span :class="item.colorClass" class="qp-btn-svg" v-if="!item.icon && item.svgSrc"><img :src="item.svgSrc" :class="item.svgClass" :style="item.svgStyle" /></span>
          <span class="flex-break"></span>
          <span :class="item.colorClass" class="qp-btn-label" v-if="item.title">{{ item.title }}</span>
          <v-fade-transition>
            <v-hover v-slot="{ hover }">
              <div
                :class="[{ 'on-hover': hover, 'icon-only': item.icon && !item.title, 'title-only': item.title && !item.icon && !item.svgSrc }, item.svgClass]"
                class="only-btn" :style="{ backgroundColor: item.color }"
                @click="toggleOnly(item.value, $event)"
              >
                <span class="only-label">ONLY</span>
              </div>
            </v-hover>
          </v-fade-transition>
        </v-btn>
      </v-hover>
    </v-btn-toggle>

    <div class="qp-ids"
         v-if="ids"
    >
      <a
          class="qp-select-list-btn excluded btn btn-primary"
          color="#024ad8"
          block
          depressed
          large
          @click="onSelectFromList"
      >
        <span class="btn-text" :title="listSelectButtonTitle">{{ listSelectButtonText }}</span>
      </a>
    </div>
    <modal
      class="selection-table-modal"
      v-if="ids && showSelectionModal"
      :header-title="'Select ' + selectionDataset"
      @close="showSelectionModal = false"
      @cancel="showSelectionModal = false"
      @submit="handleSelectionSubmit"
      :outsideClickWhitelist="modalOutsideClickWhitelist"
    >
      <div slot="body">
        <data-selection-table class="data-selection-table"
                              :page-size="20"
                              :columns="selectionTableColumns"
                              :dataset-metadata="selectionDatasetMetadata"
                              :filter-columns="selectionTableFilterColumns"
                              :data-index="selectionTableDataIndex"
                              :dataset-name="selectionDataset"
                              :display-field="selectionTableDisplayField"
                              :query-function="selectionTableQueryFunction"
                              :default-selections="selectionTableDefaultSelections"
                              nested-field-separator="|"
                              @selections-updated="handleSelectionsUpdated" />
      </div>
    </modal>
  </div>
</template>

<script>
import _ from 'lodash';
import QuickPickService from '../../services/quick-pick.service';
import FilterExpressionService from "../../modules/assurance/services/filter.expression.service";
import VueDatasetService from "../../services/vue-dataset-view.service";
import FormatterCommon from "../../app/data-formatters/formatter-common";
import Modal from "../modal/src/modal.vue";
import MetadataService from "../../services/metadata.service";
import FilterMigrationService from "../../services/filter.migration.service";
import DataSecureService from "../../modules/assurance/services/data-secure.service";
import DashboardContainer from "../dashboardContainer/src/dashboardContainer.vue";

const FILTER_TYPES = QuickPickService.getFilterTypes();

export default {
    name: 'QuickPickFilter',
    components: {
      DashboardContainer,
      'data-selection-table': () => import('../dataSelectionTable/src/dataSelectionTable.vue'),
      'modal': Modal,
    },
    props: {
      widget: {
        type: String,
        default: ''
      },
      datasetName: {
        type: String,
        default: ''
      },
      fieldName: {
        type: String,
        default: ''
      },
      initialSelection: {
        type: Array,
        default: () => undefined
      }
    },
    data() {
      return {
        onlyClicked: false,
        skipUpdate: false,
        currentSelections: this.initialSelection || [],
        quickPickSelection: this.initialSelection || [],

        metadata: undefined,
        translatedSelections: this.initialSelection || [], // generic selection objects, e.g. { id: '123', name: 'my endpoint' }, instead of using dynamic keys
        showSelectionModal: false,
        selectionTableColumns: [],
        selectionDatasetMetadata: [],
        selectionTableFilterColumns: [],
        selectionTableDataIndex: '',
        selectionTableDisplayField: '',
        selectionTableDefaultSelections: [],
        modalOutsideClickWhitelist: ['autocomplete-result', 'addFilterGroup', 'addFilter', 'center-pill', 'fa-plus-circle', 'pill', 'pillbox', 'operator', 'remove-filter', 'delete', 'listValueDisplay', 'insertMenuContent', 'addNotificationMenuContent', 'filterMenuContent', 'filterMenuListItem', 'filterMenuListItemIcon', 'filterMenuListItemText'],

      }
    },
    created: function() {
      let self = this;

      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          if (self.showSelectionModal) {
            self.showSelectionModal = false;
            e.stopImmediatePropagation();
          }
        }
      }

      document.addEventListener('keydown', escapeHandler);

      this.$once('hook:destroyed', () => {
        document.removeEventListener('keydown', escapeHandler);
      });

      MetadataService.fetchDatasetMetadataWithoutCategories().then(responseMetadata => {
        self.metadata = responseMetadata.find(config => config.id.toLowerCase() === self.selectionDataset.toLowerCase());
        if (self.metadata) {
          let columnMetadata = self.metadata.columns.find(col => col.id === self.fieldName);
          if (columnMetadata && columnMetadata.datasetId) {
            self.metadata = responseMetadata.find(config => config.id.toLowerCase() === (columnMetadata.datasetId));
          }
        }
      });

      DataSecureService.fetchMetricModels().then(function (models) {
        self.modelsMetaData = models;
        let modelKey = 'models.' + self.datasetName;
        if (!_.endsWith(modelKey, 's')) {
          modelKey += 's';
        }

        self.modelList = _.get(self.modelsMetaData, modelKey);
      });
    },
    computed: {
      qpType: function() {
        let self = this;
        return self.widget || FilterMigrationService.getQuickPickType(self.fieldName, self.datasetName);
      },
      toggles: function() {
        let self = this;
        return self.qpType === 'toggles';
      },
      ids: function() {
        let self = this;
        return self.qpType === 'ids';
      },

      listSelectButtonText: function() {
        let self = this;
        let text = `Select ${self.datasetDisplayName}...`;
        if (self.translatedSelections.length) {
          if (self.translatedSelections.length < 4) {
            text = self.translatedSelections.map(t => t.name).join(', ');
          } else {
            text = self.translatedSelections.map(t => t.name).slice(0,2).join(', ') + ' + ' + (self.translatedSelections.length - 2) + ' more';
          }
        }
        return text;
      },
      listSelectButtonTitle: function() {
        let self = this;
        let text = undefined;
        if (self.translatedSelections.length) {
            text = self.translatedSelections.map(t => t.name).join(', ');
        }
        return text;
      },
      datasetDisplayName: function() {
        let self = this;
        // hack for bridges and callcontrol
        if (self.datasetName === 'bridges') {
          return 'Bridges';
        } else if (self.datasetName === 'callcontrol') {
          return 'Call Control';
        }

        if (self.metadata) return self.metadata.name;
        else return _.startCase(self.selectionDataset);
      },
      selectionDataset: function() {
        // this logic will convert metric metadata names to dataset names
        let self = this;
        if (self.metadata) return self.metadata.id;
// todo clean this up
        let datasetName = self.datasetName;
        if (datasetName === 'bridges') {
          datasetName = 'systems';
        } else if (datasetName === 'callcontrol') {
          datasetName = 'systems';
        } else if (datasetName && !datasetName.endsWith('s') && !datasetName.startsWith('summaryview')) {
          datasetName += 's';
        }
        return datasetName;
      },
      selectionDatasetOuterFilter: function() {
        let self = this;
        let filter = '';
        if (self.datasetName === 'bridges') {
          // todo setup filter - make sure this list is correct and we maintain it over time
          // todo it would be better to add this to metadata somewhere
          filter = 'type=in=('+self.modelList.join(',')+')'
        } else if (self.datasetName === 'callcontrol') {
          // todo setup filter - make sure this list is correct and we maintain it over time
          // todo it would be better to add this to metadata somewhere
          filter = 'type=in=('+self.modelList.join(',')+')'
        }
        return filter;
      },

      items: function() {
        return _.get(FILTER_TYPES, [this.datasetName, this.fieldName], []);
      },
      excludedSelections: function() {
        return _.difference(this.items.map(item => item.value), this.quickPickSelection);
      }
    },
    watch: {
      quickPickSelection: function() {
        if (this.quickPickSelection && this.items && !this.skipUpdate) {
          let allSelected = this.quickPickSelection.length === this.items.length;
          let otherDeselected = this.excludedSelections.includes('Other');
          let qpDescriptions = this.getDescriptionsFromValues(this.quickPickSelection);
          let excludedDescriptions = this.getDescriptionsFromValues(this.excludedSelections);
// todo need to set allValues on all filters when initially loaded to migrate things if they save but don't edit the filter
          this.$emit('quick-pick-updated', this.qpType, { selections : this.quickPickSelection, allValues: this.items.map(item => item.value) },
              this.excludedSelections, qpDescriptions, excludedDescriptions, this.onlyClicked, allSelected, otherDeselected);
          this.onlyClicked = false;
        }
      },
      showSelectionModal: function(show) {
        let self = this;
        if (!show) {
          this.$emit('quick-pick-updated', self.qpType, { selections: self.translatedSelections } );
        }
      },
      initialSelection: function(selection) {
        this.skipUpdate = true;
        this.quickPickSelection = selection;
        this.$nextTick(() => { this.skipUpdate = false; });
      }
    },
    mounted () {
      let self = this;
      if (!self.initialSelection) {
        self.items.forEach(item => {
          self.quickPickSelection.push(item.value);
        });
      }
    },
    methods: {
      toggleOnly: function(value, event) {
        if (this.quickPickSelection.length === 1 && this.quickPickSelection[0] === value) {
          if (event) {
            // this is important to stop the click on "only" being treated as a click on the parent button as well
            event.stopPropagation();
          }
          this.onlyClicked = false;
          this.quickPickSelection = this.items.map(i => i.value);
        } else {
          this.only(value,event);
        }
      },
      only: function(value, event) {
        if (event) {
          // this is important to stop the click on "only" being treated as a click on the parent button as well
          event.stopPropagation();
        }
        this.onlyClicked = true;
        this.quickPickSelection = [value];
      },
      getDescriptionsFromValues: function(values) {
        let descriptions = [];
        for (let i = 0, _i = values.length; i < _i; i++) {
          let quickPick = this.items.find(item => item.value === values[i]);
          if (quickPick) {
            let desc = quickPick.legendTitle ? quickPick.legendTitle : quickPick.value;
            descriptions.push(desc);
          }
        }
        return descriptions;
      },

      onSelectFromList: function() {
        let self = this;

        function showSelectionDialog() {
          let columns = self.metadata.columns;
          // let columns = self.datasetMetadata.filter((metadata) => metadata.id === self.selectionDataset)[0].columns;
          self.selectionDatasetMetadata = self.metadata;
          self.selectionTableColumns = VueDatasetService.convertColumns(self.metadata.columns, undefined, true);
          // self.selectionTableColumns = VueDatasetService.getColumnsFromMetadata(self.selectionDataset, self.datasetMetadata, { metricMetadata: self.metadata });
          self.selectionTableFilterColumns = FilterExpressionService.getColumnsFromFilters(columns).filter(column => !column.invisible);
          self.selectionTableDataIndex = self.metadata.indexColumn;
          self.selectionTableDisplayField = self.metadata.nameColumn;
          // todo refactor to computed
          self.selectionTableDefaultSelections = self.translatedSelections.map(sel => {
            let newSel = {};
            newSel[self.selectionTableDataIndex] = sel.id;
            newSel[self.selectionTableDisplayField] = sel.name;
            return newSel;
          });

          self.showSelectionModal = true;
        }
        showSelectionDialog();
      },


      selectionTableQueryFunction: function(page, filterExpression, sorters) {
        let self = this;
        // TODO: get filter string from filterExpression AND from selectionDatasetOuterFilter
        let filter = FilterExpressionService.getFilterStringFromFilterExpression(filterExpression);
        if (self.selectionDatasetOuterFilter) {
          if (filter) {
            filter = self.selectionDatasetOuterFilter + ';(' + filter + ')';
          } else {
            filter = self.selectionDatasetOuterFilter;
          }
        }

        return new Promise((resolve, reject) => {
          let params = {
            page,
            filter,
            size: 20, // todo this should use the data-selection-table pageSize
            sort: VueDatasetService.getSortParam(sorters)
          };

          VueDatasetService.getDataset(self.selectionDataset, params).then(responseData => {
            let safeData = []; // todo this isn't used
            if (responseData.content && responseData.content.length > 0) {
              safeData = responseData.content.map(function(row) {
                return FormatterCommon.sanitize(row);
              });
            }
            resolve({
              data: responseData.content,
              last_page: responseData.totalPages,
              total_count: responseData.totalCount
            })
          });
        });
      },

      handleSelectionsUpdated: function(selections) {
        let self = this;
        self.currentSelections = selections;
      },

      handleSelectionSubmit: function() {
        let self = this;
        let selections = self.currentSelections;
        self.translatedSelections = selections.map(sel => {
          return {
            id: sel[self.selectionTableDataIndex],
            name: _.unescape(sel[self.selectionTableDisplayField]) // todo should not be escaped in the first place
          }
        });
        self.showSelectionModal = false;
      },

    }
  }
</script>

<style scoped lang="scss">
  .quick-pick-filter {
    .qp-ids {
      min-height: 37px; // bit of a hack
      margin-top: 1.5px;
      margin-bottom: 1.5px;
    }
    // UI-2501: These styles are copied/adapted from qualityFilters.vue
    .qp-btn-label {
      font-size: 8px;
      line-height: 10px;
      font-weight: 800;
      font-family: var(--font-family);
      letter-spacing: unset;
      text-transform: uppercase;
      flex-basis: 100%;
      margin: 0 -8px;
      display: inline-flex;
      justify-content: center;
    }
    .qp-btn-icon {
      font-size: 18px;
      //line-height: 20px;
      display: inline-flex;
    }
    
    .qp-select-list-btn {
      min-width: 198px;
      max-width: 100%;

      // copied from vuetify-overrides
      &.v-btn {
        text-transform: none;
        .v-btn__content {
          font-size: 14px;
          font-weight: 800;
          font-family: var(--font-family);
          letter-spacing: unset;
        }
      }


      &.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--outlined) {
        .v-btn__content {
          color: #E9E9E9;
        }
      }

      // disabled button
      &.theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
        background-color: var(--default-color) !important;
        opacity: 0.25;
      }
      // /vuetify-overrides
    }

    .v-btn-toggle {
      width: 100%;
      border-radius: var(--border-radius);
      .v-btn.selection-button {
        border-radius: 0px;
      }
    }

    .v-btn-toggle>.v-btn.selection-button:last-child {
      border-top-right-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
    }

    .v-btn-toggle>.v-btn.selection-button:first-child {
      border-top-left-radius: var(--border-radius);
      border-bottom-left-radius: var(--border-radius);
      border-left-width: 1px;
    }

    .v-btn-toggle > .v-btn.selection-button {
      border-left-width: 0;
      opacity: 1;
      padding: 0 0px;
      flex-grow: 1;
      flex-basis: 0;
    }

    .v-btn:not(.v-btn--round).v-size--default {
      height: 40px;
    }

    .v-btn-toggle:not(.v-btn-toggle--dense) .v-btn.v-size--default {
      height: 40px;
      min-width: 42px;
    }

    .v-btn.selection-button:not(.v-btn--text):not(.v-btn--outlined).v-btn--active:before {
      opacity: 0;
    }

    .v-btn.selection-button:not(.v-btn--text):not(.v-btn--outlined):not(.v-btn--active) span {
      opacity: 0.5;
    }

    .v-btn.selection-button:not(.v-btn--text):not(.v-btn--outlined):hover:before {
      opacity: 0.135;
    }

    .theme--light.v-btn.selection-button:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
      background-color: var(--filter-bg-color);
    }

    .theme--light.v-btn.selection-button:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined):not(.v-btn--active) {
      background-color: var(--active-background-color);
    }

    .only-btn {
      display: none;
      top: 38px;
      padding: 3px 5px 3px 4px;
      height: 15px;
      min-width: 30px;
      border-radius: 0px 0px 3px 3px;
      position: absolute;
      font-size: 8px;
      line-height: 10px;
      font-weight: 700;
      font-family: var(--font-family);
      letter-spacing: unset;
      text-transform: uppercase;
      color: #ffffff;
      z-index: 100;

      //&.icon-only {
      //  top: 29px;
      //}
      //
      //&.title-only {
      //  top: 24px;
      //}
      //
      //&.qp-option-svg {
      //  top: 32px;
      //  &.qp-option-green {
      //    top: 28px;
      //  }
      //}
    }

    .only-label {
      font-family: var(--font-family);
      font-weight: bold;
    }

    .only-btn.on-hover {
      text-decoration: underline;
      text-decoration-thickness: from-font;
    }
    // END copied styles

    .v-btn.selection-button.on-hover .only-btn {
      display: block;
    }

    .qp-btn-svg {
      img {
        // height needs to be set as an inline style, otherwise it breaks the capture functionality
        width: auto;
        position: relative;
        bottom: 4px;
        left: 3px;

        &.qp-option-green {
          bottom: 0;
          left: 0;
        }

        &.qp-option-mic {
          left: 4px;
          &.qp-option-green {
            left: 0;
          }
        }

        &.qp-option-touchpanel {
          bottom: 2px;
          left: 1px;
          &.qp-option-green {
            bottom: -2px;
          }
        }
      }
    }

    .flex-break {
      flex-basis: 100%;
      height: 0;
    }
  }
</style>
<style lang="scss">
  @media (max-height: 490px) {
    .wrapper {
      .quick-pick-filter {
        .selection-table-modal > .modal-wrapper > .modal-container {
          > .modal-body {
            overflow: auto!important;
          }
        }
      }
    }
  }

  .quick-pick-filter {
    // UI-2501: These styles are copied/adapted from qualityFilters.vue
    .v-btn.selection-button .v-btn__content {
      color: #253035;
      flex-wrap: wrap;
    }

    .v-btn.selection-button .v-btn__content {
      font-size: 14px;
      font-weight: 500;
      font-family: var(--font-family);
      letter-spacing: unset;
      height: 100%;
      align-content: center;
    }
    // END copied styles

    .selection-table-modal.modal-mask {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100vw;
      height: 110vh;
    }

    .selection-table-modal > .modal-wrapper > .modal-container {
      width: 1250px;
      max-width: 90vw;

      max-height: calc(100vh - 150px);
      padding: 0;

      > .modal-header {
        .header-title {
          margin-top: 10px;
        }
      }

      > .modal-body {
        margin: 0;
        border: none;
        overflow: hidden;

        .data-selection-table {
          padding: 0 10px;

          a:hover {
            text-decoration: underline;
          }

          .tabulator {
            height: calc(100vh - 150px - 204px - var(--filter-height));
            min-height: 150px;
            margin-bottom: 0;
          }

          .selections-container {
            height: calc(100vh - 150px - 204px  - var(--filter-height) - 50px);
            min-height: calc(150px - 36px);
            font-size: 12px;
          }

          .table-list-wrapper .list-container {
            padding: 0;
            max-width: unset;
          }
        }

      }

    }
    .selection-table-modal .autocomplete-result-list {
      padding-left: 0;
    }

  }




  // overrides for alerts view
  .v-application#alert-details-app .quick-pick-filter .v-btn-toggle > .v-btn.selection-button {
    border-radius: 0;
  }
  .v-application#alert-details-app .v-btn-toggle>.v-btn.v-btn.selection-button:last-child{
    border-top-right-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
  }
  .v-application#alert-details-app .v-btn-toggle>.v-btn.v-btn.selection-button:first-child {
    border-top-left-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
  }

/* SEMANTIC_TEXT RELATED CLASSES  (begin) */ 
  /* this needs to be moved as it's used in the table */
.semantic-text {
    text-transform: uppercase;
    display: flex;
    align-items: center;
    i {
      margin-right: 5px;
      font-size: 14px;
      &.small-icon {
        font-size: 8px;
      }
      &.right-icon {
        margin-right: 0;
        margin-left: 5px;
      }
    }
}

.semantic-neutral {
  color: var(--neutral2-color);
}

.semantic-critical {
    color: var(--critical-color);
}

.semantic-bad {
    color: var(--bad-color);
}

.semantic-fair {
    color: var(--fair-color);
}

.semantic-good {
    color: var(--good-color);
}

.semantic-none,
.semantic-unknown {
    color: var(--unknown-color);
}

.semantic-incall {
  color: var(--neutral-color);
}

.semantic-custom {
    color: #8085E9;
}

/* SEMANTIC_TEXT RELATED CLASSES  (end) */

.alert-state-container {
  line-height: 9px;

  button &,
  span {
    letter-spacing: unset;
    font-size: 12px;
    line-height: 9px;
    font-family: var(--font-family);
    text-transform: uppercase;
    display: flex;
    align-items: center;
  }
  i {
    font-size: 14px;
  }
}

.alert-state-ok {
  color: var(--good-color);
}
.alert-state-warn {
  color: var(--fair-color);
}
.alert-state-error {
  color: var(--bad-color);
}
.alert-state-info {
  color: var(--neutral-color); // was #0073b7; 5.22:1 on white
}
.alert-state-unknown {
  color: var(--unknown-color); // was #a7a7a7; 2.41:1 on white
}

</style>